import React, { useState, useRef, useEffect } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  makeStyles
} from '@material-ui/core'
import { useInactivePageContext } from '../hooks/InactivePageLogicContext'
import { useMutation } from '@tanstack/react-query'
import { API, graphqlOperation } from 'aws-amplify'
import { userOtpVerification, userSendSmsOtp } from 'src/graphql/mutations'

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100vw',
    maxWidth: '100vw',
    overflowX: 'hidden',
    boxSizing: 'border-box',
    padding: '37px 16.7px '
  },
  header: {},
  content: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '16px'
  },
  instruction: {
    color: '#9E9E9E',
    textAlign: 'center',
    fontSize: '16px',
    width: '100%',
    lineHeight: 1.6,
    fontWeight: 'normal'
  },
  codeInputContainer: {
    display: 'flex',
    gap: '8px',
    marginBottom: theme.spacing(3),
    width: '100%',
    justifyContent: 'center',
    boxSizing: 'border-box'
  },
  codeInput: {
    flex: 1,
    width: '100%',
    backgroundColor: 'white',

    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#1E91CF',
        borderWidth: '2px',
        borderRadius: '8px'
      },
      '&:hover fieldset': {
        borderColor: '#1E91CF'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#1E91CF',
        borderWidth: '2px'
      }
    },
    '& input': {
      textAlign: 'center',
      fontSize: '1.25rem',
      fontWeight: 500,
      padding: '12px 4px',
      height: '50px',
      width: '100%',
      boxSizing: 'border-box'
    }
  },
  buttonContainer: {
    display: 'flex',
    gap: '16px',
    width: '100%'
  },
  actionButton: {
    width: '100px',
    padding: '10px 0',
    borderRadius: '10px'
  },
  resendButton: {
    width: '100px',
    padding: '10px 0',
    border: '1px solid #202020',
    backgroundColor: 'white',
    borderRadius: '10px'
  },
  resendButtonDisabled: {
    width: '100px',
    padding: '10px 0',
    border: '1px solid #202020',
    backgroundColor: '#C5C5C5',
    borderRadius: '10px'
  },
  timerContainer: {
    display: 'flex',
    gap: '16px',
    width: '100%',
    flexDirection: 'column'
  }
}))

const VerificationPage = () => {
  const classes = useStyles()
  const [code, setCode] = useState(['', '', '', ''])
  const inputRefs = [useRef(null), useRef(null), useRef(null), useRef(null)]
  const [error, setError] = useState(null)
  const [resendEnable, setResendEnable] = useState(false)
  const [loading, setLoading] = useState(false)

  const {
    mobileNo,
    setMobileNo,
    setVerificationPage,
    verificationPlatform,
    mutateUpdateProfile,
    setOpenMobileDialog,
    setFeedback,
    verificationPage,
    forgotPassword,
    setForgotPassword,
    timer,
    isUpdatingProfile,
    setTimer
  } = useInactivePageContext()

  const handleBack = (open) => {
    setOpenMobileDialog(open)
    setVerificationPage((prev) => ({ ...prev, open: false }))
    setMobileNo('')
  }

  const handleChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)

    // Auto-focus next input
    if (value && index < 3) {
      if (inputRefs[index + 1].current) {
        inputRefs[index + 1].current.focus()
      }
    }
  }

  const handleKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      if (inputRefs[index - 1].current) {
        inputRefs[index - 1].current.focus()
      }
    }
  }

  const handlePaste = (e) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').slice(0, 4)
    const newCode = pastedData.split('').filter((char) => /^\d$/.test(char))

    if (newCode.length > 0) {
      const updatedCode = [...code]
      newCode.forEach((char, i) => {
        if (i < 4) updatedCode[i] = char
      })
      setCode(updatedCode)

      // Focus the next empty input or last input
      const nextIndex = Math.min(newCode.length, 3)
      if (inputRefs[nextIndex].current) {
        inputRefs[nextIndex].current.focus()
      }
    }
  }

  useEffect(() => {
    // Timer countdown
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer((prev) => prev - 1)
      }, 1000)
      return () => clearInterval(interval)
    }

    if (timer <= 0) {
      setResendEnable(true)
    }
  }, [timer])

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins.toString().padStart(2, '0')}:${secs
      .toString()
      .padStart(2, '0')}`
  }

  const {
    mutate: mutateUserOtpVerification,
    isPending: isVerifyingOtp
  } = useMutation({
    mutationFn: async () => {
      setLoading(true)
      const res = await API.graphql(
        graphqlOperation(userOtpVerification, {
          mobileNo: `60${mobileNo}`,
          platform: verificationPlatform,
          code: code.join('')
        })
      )
      return res?.data?.userOtpVerification
    },
    async onSuccess(data) {
      if (data?.Status) {
        if (verificationPage.update) {
          const res = await mutateUpdateProfile({
            email: null,
            mobileNo: `60${mobileNo}`
          })
          if (res?.Status === true) {
            setFeedback((prev) => ({ ...prev, customerMobileNo: mobileNo }))
            handleBack(false)
            setForgotPassword(false)
          }
        } else {
          setFeedback((prev) => ({ ...prev, customerMobileNo: mobileNo }))
          handleBack(false)
          setForgotPassword(false)
        }
        setLoading(false)
      } else {
        setLoading(false)
        setError(data?.Message || 'Failed to verify OTP')
      }
    },
    onError(error) {
      setLoading(false)
      console.log('error', error)
      setError(error?.message || 'Failed to verify OTP')
      setCode(['', '', '', ''])
    }
  })

  const {
    mutate: mutateUserSendSmsOtp,
    isPending: isSendingSmsOtp
  } = useMutation({
    mutationFn: async (mobileNo) => {
      const res = await API.graphql(
        graphqlOperation(userSendSmsOtp, {
          mobileNo: `60${mobileNo}`,
          platform: verificationPlatform,
          forgotPassword: forgotPassword
        })
      )
      setTimer(res?.data?.userSendSmsOtp?.RemainingTime || 300)
      return res?.data?.userSendSmsOtp
    },
    onSuccess(data) {
      if (data?.Status === true) {
        setTimer(timer || 300)
      } else {
        setError(data?.Message || 'Failed to send OTP')
        setCode(['', '', '', ''])
      }
    },
    onError(error) {
      setError(error?.message || 'Failed to send OTP')
      setCode(['', '', '', ''])
    }
  })

  const handleSubmit = async () => {
    await mutateUserOtpVerification()
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.content}>
        {' '}
        <Typography className={classes.instruction}>
          {`Enter 4 digit code verification Code sent via Whatsapp message to +60${mobileNo}`}
        </Typography>
        <Box className={classes.codeInputContainer}>
          {code.map((digit, index) => (
            <TextField
              key={index}
              inputRef={inputRefs[index]}
              placeholder="-"
              type="text"
              inputProps={{
                maxLength: 1,
                inputMode: 'numeric',
                'aria-label': `Digit ${index + 1}`
              }}
              value={digit}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              variant="outlined"
              className={classes.codeInput}
            />
          ))}
        </Box>
        <Box className={classes.buttonContainer}>
          <Button
            disabled={isVerifyingOtp || isUpdatingProfile}
            onClick={() => {
              handleBack(true)
              setFeedback((prev) => ({
                ...prev,
                customerMobileNo: ''
              }))
            }}
            className={classes.actionButton}
            variant="contained"
            color="primary">
            Back
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={
              code.join('').length !== 4 ||
              isVerifyingOtp ||
              isSendingSmsOtp ||
              isUpdatingProfile ||
              loading
            }
            className={classes.actionButton}>
            Submit
          </Button>
        </Box>
        <Box className={classes.buttonContainer}>
          <Button
            onClick={() => {
              mutateUserSendSmsOtp(mobileNo)
            }}
            disabled={
              !resendEnable ||
              isVerifyingOtp ||
              isSendingSmsOtp ||
              isUpdatingProfile
            }
            className={
              !resendEnable || isVerifyingOtp
                ? classes.resendButtonDisabled
                : classes.resendButton
            }
            style={{ textTransform: 'none', color: '#71717A' }}
            variant="outlined"
            color="primary">
            Resend
          </Button>
        </Box>
        <Box className={classes.timerContainer}>
          <Typography>
            {error && <span style={{ color: 'red' }}>{error}</span>}
          </Typography>
          <Typography>Resend new code in</Typography>
          <Typography
            className={classes.timer}
            style={{
              color: '#1E91D0',
              fontSize: '16px',
              fontWeight: 'normal'
            }}>
            {formatTime(timer)}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export default VerificationPage
